import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown } from 'antd';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import * as CookieConsent from 'vanilla-cookieconsent';
import { SettingOutlined } from '@ant-design/icons';
import UserMenuDropdown from '@components/UserMenuDropdown';
import SearchBar from '@components/SearchBar';
import CampaignBreadcrumbs from '@components/CampaignBreadcrumbs';
import {
  toggleBootcampModal,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import { NotificationFeed } from '@components/NotificationFeed';
import { useCurrentWorkspace } from '@hooks/workspace';
import { nonNull } from '@helpers/non-null';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import {
  HelpIcon,
  InfoBookIcon,
  InfoBootcampIcon,
  InfoChatIcon,
  InfoMessageSquareIcon,
  InfoNewsFillIcon,
  UploadIcon
} from '@icons';

function AppHeader() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const isShowTourPause = searchParams.get('startTour') === 'tour-paused';
  const isShowTourUploadAsset =
    searchParams.get('startTour') === 'upload-asset';
  const { onStartTour, tourData } = useOnboardingProcess();
  const { campaignId, folderId } = useParams();
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false);
  const [currentWorkspace] = useCurrentWorkspace(false);

  useEffect(() => {
    if (!isShowTourPause) return;
    setIsOpenDropdown(true);
    const timeoutId = setTimeout(() => {
      onStartTour({
        type: 'tour-paused',
        onClose: () => {
          setIsOpenDropdown(false);
        }
      });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [isShowTourPause, onStartTour, setIsOpenDropdown]);

  useEffect(() => {
    if (!isShowTourUploadAsset) return;
    const timeoutId = setTimeout(() => {
      onStartTour({
        type: 'upload-asset'
      });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [isShowTourUploadAsset, onStartTour]);

  const menuItems: ItemType[] = useMemo(() => {
    return [
      {
        key: 'chatWithUs',
        label: 'Chat with us',
        icon: <InfoChatIcon />,
        onClick: () => {
          window.open(
            'https://www.streamwork.com/contact#hs-chat-open',
            '_blank'
          );
        }
      },
      {
        type: 'divider'
      },
      {
        key: 'bootcamp',
        label: 'Bootcamp',
        icon: <InfoBootcampIcon />,
        onClick: () => {
          setIsOpenDropdown(false);
          dispatch(
            toggleBootcampModal({
              visible: true
            })
          );
        }
      },
      {
        key: 'helpCenter',
        label: 'Help center',
        icon: <InfoBookIcon />,
        onClick: () => {
          window.open('https://help.streamwork.com/knowledge', '_blank');
        }
      },
      {
        key: 'contactSupport',
        label: 'Contact support',
        icon: <HelpIcon />,
        onClick: () => {
          window.open('https://www.streamwork.com/contact', '_blank');
        }
      },
      {
        key: 'submitFeedback',
        label: 'Submit feedback',
        icon: <InfoMessageSquareIcon />,
        onClick: () => {
          window.open('https://www.streamwork.com/contact', '_blank');
        }
      },
      {
        key: 'privacySettings',
        label: 'Privacy settings',
        icon: (
          <span
            style={{
              display: 'inline-block',
              width: 24,
              height: 24,
              textAlign: 'center',
              lineHeight: '20px'
            }}
          >
            <SettingOutlined style={{ fontSize: 20 }} />
          </span>
        ),
        onClick: () => {
          CookieConsent.reset(true);
          window.location.reload();
        }
      },
      {
        type: 'divider'
      },
      {
        key: 'whatNew',
        label: 'What’s new',
        icon: <InfoNewsFillIcon />,
        onClick: () => {
          window.open('https://www.streamwork.com/blog', '_blank');
        }
      }
    ];
  }, [setIsOpenDropdown, dispatch]);

  const onOpenChangeDropdown = useCallback(
    (value: boolean) => {
      if (tourData?.type === 'tour-paused') return;
      setIsOpenDropdown(value);
    },
    [tourData?.type, setIsOpenDropdown]
  );

  return (
    <div className="header_container">
      <CampaignBreadcrumbs showSearch={showSearch} />
      <SearchBar showSearch={showSearch} setShowSearch={setShowSearch} />
      <Button
        type="primary"
        className="header_container__btn tour-upload-asset"
        disabled={!currentWorkspace}
        onClick={() =>
          dispatch(
            toggleUploadAssetModal({
              visible: true,
              postAction: 'redirect-to-campaign',
              workspaceId: nonNull(currentWorkspace).id,
              campaignId,
              folderId
            })
          )
        }
      >
        <UploadIcon />
        Upload
      </Button>
      <NotificationFeed />
      <Dropdown
        menu={{
          items: menuItems
        }}
        open={isOpenDropdown}
        onOpenChange={onOpenChangeDropdown}
        placement="bottomRight"
        overlayClassName="help_dropdown header_help_dropdown tour-paused"
        trigger={['click']}
      >
        <Button
          className="header_container__help tour-paused-btn"
          type="text"
          onClick={(e) => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }}
        >
          <HelpIcon />
        </Button>
      </Dropdown>
      <UserMenuDropdown />
    </div>
  );
}

export default AppHeader;
